import React from 'react';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { filterTrackingPhrases, isOnlyTrackingPhrases } from '@/utils/transcriptFiltering';

export interface ChatBubbleProps {
  message: string;
  role: 'user' | 'assistant' | 'system';
  timestamp?: string;
  startTime?: number;
  endTime?: number;
  secondsFromStart?: number;
  showTiming?: boolean;
  isActive?: boolean;
  onSeek?: (seconds: number) => void;
  className?: string;
}

export const ChatBubble: React.FC<ChatBubbleProps> = ({
  message,
  role,
  timestamp,
  startTime,
  endTime,
  secondsFromStart,
  showTiming = true,
  isActive = false,
  onSeek,
  className
}) => {
  // Filter tracking phrases from assistant messages
  const filteredMessage = role === 'assistant' ? filterTrackingPhrases(message) : message;
  
  // Skip rendering if the message contains only tracking phrases
  if (role === 'assistant' && isOnlyTrackingPhrases(message)) {
    return null;
  }

  const isUser = role === 'user';
  const isAssistant = role === 'assistant';
  const isSystem = role === 'system';

  const getRoleLabel = () => {
    switch (role) {
      case 'user':
        return 'Candidate';
      case 'assistant':
        return 'Interviewer';
      case 'system':
        return 'System';
      default:
        return role.charAt(0).toUpperCase() + role.slice(1);
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const millis = Math.floor((seconds % 1) * 100);
    return `${mins}:${secs.toString().padStart(2, '0')}.${millis.toString().padStart(2, '0')}`;
  };

  const getTimingDisplay = (): string | null => {
    if (secondsFromStart !== undefined && startTime !== undefined && endTime !== undefined) {
      const duration = (endTime - startTime) / 1000; // Convert to seconds
      const endSeconds = secondsFromStart + duration;
      return `${formatTime(secondsFromStart)} - ${formatTime(endSeconds)}`;
    }
    return null;
  };

  const canSeek = !!onSeek && secondsFromStart !== undefined && secondsFromStart !== null;

  const handleSeek = () => {
    if (canSeek) onSeek!(secondsFromStart!);
  };

  return (
    <div
      className={cn(
        "flex mb-2",
        isUser ? "justify-end" : "justify-start",
        className
      )}
    >
      <div
        role={canSeek ? "button" : undefined}
        tabIndex={canSeek ? 0 : undefined}
        aria-label={canSeek ? `Play recording from ${formatTime(secondsFromStart!)}` : undefined}
        onClick={canSeek ? handleSeek : undefined}
        onKeyDown={
          canSeek
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleSeek();
                }
              }
            : undefined
        }
        className={cn(
          "max-w-[80%] rounded-lg p-2 relative text-xs",
          canSeek && "cursor-pointer transition-shadow hover:ring-2 hover:ring-primary/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
          isActive && "ring-2 ring-primary",
          isUser && [
            "bg-secondary/20",
            "text-foreground",
            "border border-border-border"
          ],
          isAssistant && [
            "bg-muted",
            "text-foreground dark:text-ink-faint",
            "border border-border"
          ],
          isSystem && [
            "bg-mid-bg-mid-bg/20",
            "text-mid",
            "border border-mid-line"
          ]
        )}
      >
        <div className={cn(
          "font-semibold mb-1 text-xs",
          isUser && "text-foreground",
          isAssistant && "text-foreground dark:text-ink-faint",
          isSystem && "text-mid"
        )}>
          {getRoleLabel()}
        </div>
        
        <div className="whitespace-pre-wrap break-words text-xs">
          {filteredMessage}
        </div>
        
        {(timestamp || getTimingDisplay()) && (
          <div className={cn(
            "text-xs mt-1 flex items-center justify-between gap-2",
            isUser && "text-foreground",
            isAssistant && "text-muted-foreground dark:text-ink-faint",
            isSystem && "text-mid"
          )}>
            {timestamp && (
              <span>
                {(() => {
                  try {
                    const date = new Date(timestamp);
                    if (isNaN(date.getTime())) {
                      return timestamp; // Return the original timestamp if it's not a valid date
                    }
                    return format(date, "h:mm:ss a");
                  } catch (error) {
                    return timestamp; // Return the original timestamp if formatting fails
                  }
                })()}
              </span>
            )}
            {showTiming && getTimingDisplay() && (
              <span className="font-medium">
                {getTimingDisplay()}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export interface ChatConversationProps {
  messages: Array<{
    role: string;
    message: string;
    timestamp?: string;
    time?: number;
    endTime?: number;
    secondsFromStart?: number;
  }>;
  showTiming?: boolean;
  currentTime?: number;
  onSeek?: (seconds: number) => void;
  className?: string;
}

export const ChatConversation: React.FC<ChatConversationProps> = ({
  messages,
  showTiming = true,
  currentTime,
  onSeek,
  className
}) => {
  const normalizeRole = (role: string): 'user' | 'assistant' | 'system' => {
    if (role === 'user' || role === 'human') return 'user';
    if (role === 'assistant' || role === 'ai') return 'assistant';
    return 'system';
  };

  // Last message whose speech has already started is the one currently playing
  const activeIndex = React.useMemo(() => {
    if (currentTime === undefined) return -1;
    let index = -1;
    for (let i = 0; i < messages.length; i++) {
      const start = messages[i].secondsFromStart;
      if (start === undefined || start === null) continue;
      if (currentTime >= start) index = i;
      else break;
    }
    return index;
  }, [messages, currentTime]);

  return (
    <div className={cn("space-y-2", className)}>
      {messages.map((message, index) => {
        const normalizedRole = normalizeRole(message.role);
        
        // Skip assistant messages that contain only tracking phrases
        if (normalizedRole === 'assistant' && isOnlyTrackingPhrases(message.message)) {
          return null;
        }
        
        return (
          <ChatBubble
            key={index}
            message={message.message}
            role={normalizedRole}
            timestamp={message.timestamp}
            startTime={message.time}
            endTime={message.endTime}
            secondsFromStart={message.secondsFromStart}
            showTiming={showTiming}
            isActive={index === activeIndex}
            onSeek={onSeek}
          />
        );
      })}
    </div>
  );
};